import type { CSSProperties } from 'react'

interface PawPrintsProps {
  /** Where the trail starts, as an x offset from the parent's origin. */
  from: number
  /** Where it ends. */
  to: number
  /** How long the leg of travel being traced takes, in milliseconds. */
  travelMs: number
  /** Fraction of travelMs at which he sets off along this leg. */
  startAt?: number
  /** Fraction by which he has finished it. */
  endAt?: number
  count?: number
  /** How long each print lingers before it has faded away. */
  lifeMs?: number
}

/**
 * Prints left behind along one leg of the dog's journey.
 *
 * Positions are worked out here rather than in CSS because they depend on the
 * viewport, and each print is delayed to roughly when he passes it, so they
 * appear under him rather than all at once.
 *
 * The artwork points its toes up, so each print is turned to face the way he is
 * going - which is worked out from the direction of travel rather than passed
 * in, so a trail cannot end up pointing the wrong way.
 */
export function PawPrints({
  from,
  to,
  travelMs,
  startAt = 0,
  endAt = 1,
  count = 9,
  lifeMs = 1400,
}: PawPrintsProps) {
  const heading = to < from ? -90 : 90

  const prints = Array.from({ length: count }, (_, i) => {
    const along = (i + 1) / (count + 1)
    return {
      x: from + (to - from) * along,
      delay: travelMs * (startAt + (endAt - startAt) * along),
      // Alternate either side of the line, so it reads as a gait.
      y: i % 2 === 0 ? -5 : 6,
    }
  })

  return (
    <>
      {prints.map((print, i) => (
        <span
          key={i}
          className="animate-paw absolute"
          style={
            {
              transform: `translate3d(${print.x}px, ${print.y}px, 0) rotate(${heading}deg)`,
              '--paw-delay': `${print.delay}ms`,
              '--paw-life': `${lifeMs}ms`,
            } as CSSProperties
          }
        >
          <svg viewBox="0 0 24 22" className="h-3 w-3.5" aria-hidden="true">
            <ellipse cx="12" cy="15" rx="7" ry="6" fill="#a97038" />
            <circle cx="4" cy="7" r="2.6" fill="#a97038" />
            <circle cx="10" cy="4" r="2.8" fill="#a97038" />
            <circle cx="16.5" cy="4.5" r="2.7" fill="#a97038" />
            <circle cx="21" cy="9" r="2.4" fill="#a97038" />
          </svg>
        </span>
      ))}
    </>
  )
}
